import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import {
  api,
  type WeekCompassAxis,
  type WeekPlanMeta,
  type WeekSlot,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import FunnelMixBars from "@/components/FunnelMixBars";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";

const KIND_OPTS = [
  { value: "carousel", label: "karuzela" },
  { value: "feed_post", label: "post" },
  { value: "reel_brief", label: "rolka (brief)" },
  { value: "story_brief", label: "relacja (brief)" },
];
const FUNNEL_OPTS = [
  { value: "tofu", label: "TOFU" },
  { value: "mofu", label: "MOFU" },
  { value: "bofu", label: "BOFU" },
];
const OWNER_OPTS = [
  { value: "arek", label: "Arek" },
  { value: "kasia", label: "Kasia" },
];
const HOPE_OPTS = [
  { value: "echo", label: "echo (ból)" },
  { value: "balance", label: "balans" },
  { value: "wings", label: "skrzydła" },
];
const PHASE_OPTS = [
  { value: "sprzedaz", label: "sprzedaż" },
  { value: "przerwa", label: "przerwa" },
  { value: "zasieg", label: "zasięg" },
  { value: "edukacja", label: "edukacja" },
  { value: "warmup", label: "warm-up" },
];
const DAY_OPTS = [
  { value: "0", label: "pon" },
  { value: "1", label: "wt" },
  { value: "2", label: "śr" },
  { value: "3", label: "czw" },
  { value: "4", label: "pt" },
  { value: "5", label: "sob" },
  { value: "6", label: "nd" },
];

function currentWeek() {
  const d = new Date();
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function kindShort(kind?: string) {
  switch (kind) {
    case "carousel":
      return "kar.";
    case "feed_post":
      return "post";
    case "reel_brief":
      return "rolka";
    case "story_brief":
      return "rel.";
    default:
      return kind || "?";
  }
}

function kindLabel(kind?: string) {
  return KIND_OPTS.find((o) => o.value === kind)?.label || kind || "slot";
}

function funnelBadgeVariant(funnel?: string): "secondary" | "brand" | "warning" | "outline" {
  if (funnel === "mofu") return "brand";
  if (funnel === "bofu") return "warning";
  if (funnel === "tofu") return "secondary";
  return "outline";
}

function hopeTitle(hope?: string) {
  if (hope === "echo") return "echo";
  if (hope === "wings") return "skrzydła";
  return "balans";
}

function axisDotClass(status?: string) {
  if (status === "fail") return "bg-destructive";
  if (status === "warn") return "bg-warning";
  return "bg-emerald-600";
}

function countAxes(axes: WeekCompassAxis[]) {
  let fail = 0;
  let warn = 0;
  let ok = 0;
  for (const a of axes) {
    if (a.status === "fail") fail += 1;
    else if (a.status === "warn") warn += 1;
    else ok += 1;
  }
  return { fail, warn, ok };
}

function slotKey(slot: WeekSlot, idx: number) {
  return slot.id || `tmp-${slot.weekday}-${idx}`;
}

function statusShort(status?: string) {
  if (!status || status === "empty") return "";
  if (status === "generated") return "gen";
  if (status === "filmed") return "film";
  if (status === "needs_publish") return "pub";
  if (status === "published") return "live";
  return status.slice(0, 4);
}

function truncate(text: string, max = 120) {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

export default function PlanTygodnia() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [week, setWeek] = useState(currentWeek);
  const planQ = useQuery({ queryKey: ["week-plan", week], queryFn: () => api.weekPlan(week) });
  const plan = planQ.data;
  const [slots, setSlots] = useState<WeekSlot[] | null>(null);
  const [meta, setMeta] = useState<WeekPlanMeta | null>(null);
  const [compassOpen, setCompassOpen] = useState(false);
  const [showOkAxes, setShowOkAxes] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const shown = slots || plan?.slots || [];
  const shownMeta: WeekPlanMeta = meta || plan?.meta || {};

  useEffect(() => {
    setSlots(null);
    setMeta(null);
    setSelectedKey(null);
    setCompassOpen(false);
    setMetaOpen(false);
  }, [week]);

  const saveM = useMutation({
    mutationFn: () => api.weekPlanSave(week, shown, shownMeta),
    onSuccess: (res) => {
      setSlots(null);
      setMeta(null);
      setSelectedKey(null);
      qc.setQueryData(["week-plan", week], res);
    },
  });
  const approveM = useMutation({
    mutationFn: () => api.weekPlanApprove(week, true),
    onSuccess: (res) => {
      setSlots(null);
      setMeta(null);
      setSelectedKey(null);
      qc.setQueryData(["week-plan", week], res);
    },
  });
  const filmedM = useMutation({
    mutationFn: (id: string) => api.contentItemPatch(id, { filmed: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["week-plan", week] }),
  });
  const askM = useMutation({
    mutationFn: () => api.weekPlanAskTeam(week),
    onSuccess: (res) => {
      navigate(res.href || "/zespol-ai");
    },
  });

  const days = plan?.day_labels || ["pon", "wt", "śr", "czw", "pt", "sob", "nd"];
  const mix = plan?.mix_actual?.pct || {};
  const target = plan?.mix_target || { tofu: 40, mofu: 40, bofu: 20 };
  const compass = plan?.compass;
  const axes = compass?.axes || [];
  const counts = countAxes(axes);
  const overall = compass?.overall || "";
  const themeLine = shownMeta.theme || plan?.theme?.theme || "";

  const byDay = useMemo(() => {
    const map: Record<number, { slot: WeekSlot; idx: number }[]> = {};
    for (let i = 0; i < 7; i += 1) map[i] = [];
    shown.forEach((slot, idx) => {
      const day = Number(slot.weekday || 0);
      if (!map[day]) map[day] = [];
      map[day].push({ slot, idx });
    });
    return map;
  }, [shown]);

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    const found = shown.find((s, i) => slotKey(s, i) === selectedKey);
    if (!found) return null;
    const idx = shown.findIndex((s, i) => slotKey(s, i) === selectedKey);
    return { slot: found, idx };
  }, [selectedKey, shown]);

  const visibleAxes = useMemo(() => {
    const filtered = showOkAxes ? axes : axes.filter((a) => a.status === "fail" || a.status === "warn");
    const rank = (s?: string) => (s === "fail" ? 0 : s === "warn" ? 1 : 2);
    return [...filtered].sort((a, b) => rank(a.status) - rank(b.status));
  }, [axes, showOkAxes]);

  function patchSlot(idx: number, fields: Partial<WeekSlot>) {
    const next = shown.map((slot, i) => (i === idx ? { ...slot, ...fields } : slot));
    setSlots(next);
  }

  function patchMeta(fields: Partial<WeekPlanMeta>) {
    setMeta({ ...shownMeta, ...fields });
  }

  function addSlot(day: number) {
    const next: WeekSlot = {
      weekday: day,
      kind: "carousel",
      funnel_stage: "mofu",
      owner: "arek",
      hope_axis: "balance",
      status: "empty",
      theme: shownMeta.theme || "",
      pain_id: shownMeta.pain_id || "",
      tool_id: shownMeta.tool_id || "",
    };
    const nextSlots = [...shown, next];
    setSlots(nextSlots);
    setSelectedKey(slotKey(next, nextSlots.length - 1));
  }

  const rocksText = (shownMeta.rocks || []).join("\n");
  const verdictLabel = overall === "ok" ? "OK" : overall === "fail" ? "Do naprawy" : overall === "warn" ? "Uwaga" : "…";
  const summaryText = truncate(compass?.summary || "Ładowanie…");
  const counterLine =
    counts.fail || counts.warn
      ? `${counts.fail} do naprawy · ${counts.warn} ostrzeżeń`
      : overall
        ? "7/7 osi OK"
        : "";

  const editor = (
    <Card className="border-border bg-white shadow-sm">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">
            {selected
              ? `${kindLabel(selected.slot.kind)} · ${days[selected.slot.weekday] || "?"} · ${
                  selected.slot.owner === "kasia" ? "Kasia" : "Arek"
                }`
              : "Edycja slotu"}
          </CardTitle>
          {!selected ? (
            <p className="mt-1 text-sm text-muted-foreground">Kliknij slot w siatce, żeby edytować.</p>
          ) : null}
        </div>
        {selected ? (
          <Button size="sm" variant="ghost" onClick={() => setSelectedKey(null)} aria-label="Zamknij">
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </CardHeader>
      {selected ? (
        <CardContent className="space-y-2">
          <Select
            value={selected.slot.kind}
            onChange={(value) => patchSlot(selected.idx, { kind: value })}
            options={KIND_OPTS}
          />
          <Select
            value={selected.slot.funnel_stage}
            onChange={(value) => patchSlot(selected.idx, { funnel_stage: value })}
            options={FUNNEL_OPTS}
          />
          <Select
            value={selected.slot.owner}
            onChange={(value) => patchSlot(selected.idx, { owner: value })}
            options={OWNER_OPTS}
          />
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Echo / skrzydła</span>
            <Select
              value={selected.slot.hope_axis || "balance"}
              onChange={(value) => patchSlot(selected.idx, { hope_axis: value })}
              options={HOPE_OPTS}
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Temat slotu</span>
            <Input
              value={selected.slot.theme || ""}
              onChange={(e) => patchSlot(selected.idx, { theme: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Ból</span>
            <Input
              placeholder="id bólu, np. B17"
              value={selected.slot.pain_id || ""}
              onChange={(e) => patchSlot(selected.idx, { pain_id: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Narzędzie</span>
            <Input
              placeholder="tool z produktu"
              value={selected.slot.tool_id || ""}
              onChange={(e) => patchSlot(selected.idx, { tool_id: e.target.value })}
            />
          </div>
          <div className="pt-2">
            {selected.slot.owner === "arek" && selected.slot.content_item_id ? (
              <Link className="text-sm text-brand underline" to="/karuzele">
                Otwórz studio
              </Link>
            ) : null}
            {selected.slot.owner === "kasia" && selected.slot.content_item_id ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => filmedM.mutate(selected.slot.content_item_id || "")}
              >
                Nagrane
              </Button>
            ) : null}
            {!selected.slot.content_item_id ? (
              <p className="text-xs text-muted-foreground">Pojawi się po zatwierdzeniu tygodnia.</p>
            ) : null}
          </div>
        </CardContent>
      ) : null}
    </Card>
  );

  return (
    <div className="-mx-2 rounded-2xl border border-border bg-[#f7f8fa] p-4 text-foreground shadow-sm sm:-mx-0 sm:p-6">
      <PageHeader
        title="Plan tygodnia"
        subtitle="Kompas niedzielny. Najpierw werdykt, potem siatka."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input className="w-28 bg-white" value={week} onChange={(e) => setWeek(e.target.value)} />
            <Button variant="outline" onClick={() => saveM.mutate()} disabled={saveM.isPending}>
              Zapisz
            </Button>
            <Button variant="outline" onClick={() => askM.mutate()} disabled={askM.isPending}>
              Zapytaj zespół
            </Button>
            <Button variant="brand" onClick={() => approveM.mutate()} disabled={approveM.isPending}>
              Zatwierdź tydzień
            </Button>
          </div>
        }
      />

      {askM.isError ? (
        <p className="mb-3 text-sm text-destructive">
          {(askM.error as Error)?.message || "Nie udało się odpalić narady."}
        </p>
      ) : null}

      <Card
        className={cn(
          "mb-4 border shadow-sm",
          overall === "ok" && OP_SURFACE.result.box,
          overall === "warn" && OP_SURFACE.review.box,
          overall === "fail" && OP_SURFACE.danger.box,
          !overall && OP_SURFACE.work.box,
        )}
      >
        <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <OpRoleBadge
                tone={overall === "ok" ? "result" : overall === "warn" ? "review" : overall === "fail" ? "danger" : "work"}
                label={verdictLabel}
              />
              <Badge variant="outline">{plan?.status === "approved" ? "Zatwierdzony" : "Szkic"}</Badge>
              {counterLine ? <span className="text-xs text-muted-foreground">{counterLine}</span> : null}
            </div>
            <p className="text-sm" title={compass?.summary || undefined}>
              {summaryText}
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="flex flex-wrap gap-1.5">
              {axes.map((axis) => (
                <button
                  key={axis.id}
                  type="button"
                  title={axis.label}
                  className={cn("h-3 w-3 rounded-full ring-offset-2 hover:ring-2 hover:ring-brand", axisDotClass(axis.status))}
                  onClick={() => setCompassOpen(true)}
                />
              ))}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => setCompassOpen((v) => !v)}
            >
              {compassOpen ? "Ukryj szczegóły" : "Szczegóły kompasu"}
              {compassOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </CardContent>
        {compassOpen ? (
          <CardContent className="space-y-2 border-t border-border pt-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={showOkAxes} onChange={(e) => setShowOkAxes(e.target.checked)} />
              Pokaż też OK
            </label>
            {visibleAxes.length === 0 ? (
              <p className="text-sm text-muted-foreground">Brak problemów do pokazania.</p>
            ) : (
              visibleAxes.map((axis) => (
                <div key={axis.id} className="flex gap-2 rounded-md border border-border bg-white px-3 py-2">
                  <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", axisDotClass(axis.status))} />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium">{axis.label}</span>
                      <span className="text-[11px] uppercase text-muted-foreground">{axis.status}</span>
                    </div>
                    <p className="text-sm text-foreground/90">{axis.detail}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        ) : null}
      </Card>

      <Card className="mb-4 border-border bg-white shadow-sm">
        <CardContent className="py-4">
          <FunnelMixBars
            mix={{ pct: mix, target, warning: plan?.mix_warning || "" }}
            themeLine={themeLine ? `Temat: ${themeLine}` : undefined}
          />
        </CardContent>
      </Card>

      <Card className="mb-4 border-border bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Kierunek tygodnia</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">Temat</span>
              <Input
                placeholder="np. telefon od mamy"
                value={shownMeta.theme || ""}
                onChange={(e) => patchMeta({ theme: e.target.value })}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">Jeden ból</span>
              <Input
                placeholder="id bólu, np. B17"
                value={shownMeta.pain_id || ""}
                onChange={(e) => patchMeta({ pain_id: e.target.value })}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">Fokus Arka</span>
              <Input
                placeholder="np. 3 karuzele + paczki"
                value={shownMeta.arek_focus || ""}
                onChange={(e) => patchMeta({ arek_focus: e.target.value })}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs text-muted-foreground">Fokus Kasi</span>
              <Input
                placeholder="np. 2 MOFU + 2 TOFU"
                value={shownMeta.kasia_focus || ""}
                onChange={(e) => patchMeta({ kasia_focus: e.target.value })}
              />
            </label>
          </div>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setMetaOpen((v) => !v)}>
            {metaOpen ? "Mniej" : "Więcej ustawień tygodnia"}
            {metaOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
          {metaOpen ? (
            <div className="grid gap-3 border-t border-border pt-3 md:grid-cols-2">
              <label className="space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Narzędzie z produktu</span>
                <Input value={shownMeta.tool_id || ""} onChange={(e) => patchMeta({ tool_id: e.target.value })} />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Książka (źródło)</span>
                <Input value={shownMeta.book_id || ""} onChange={(e) => patchMeta({ book_id: e.target.value })} />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Eksperyment (max 1)</span>
                <Input
                  placeholder="max jeden test konwersji"
                  value={shownMeta.experiment || ""}
                  onChange={(e) => patchMeta({ experiment: e.target.value })}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Faza cyklu</span>
                <Select
                  value={shownMeta.cycle_phase || "edukacja"}
                  onChange={(value) => patchMeta({ cycle_phase: value })}
                  options={PHASE_OPTS}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-xs text-muted-foreground">Dzień bufora</span>
                <Select
                  value={String(shownMeta.buffer_day ?? 5)}
                  onChange={(value) => patchMeta({ buffer_day: Number(value) })}
                  options={DAY_OPTS}
                />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="text-xs text-muted-foreground">Skały miesiąca (max 3, jedna na linię)</span>
                <textarea
                  className="min-h-[72px] w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                  value={rocksText}
                  onChange={(e) =>
                    patchMeta({
                      rocks: e.target.value
                        .split("\n")
                        .map((x) => x.trim())
                        .filter(Boolean)
                        .slice(0, 3),
                    })
                  }
                />
              </label>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="xl:grid xl:grid-cols-[1fr_22rem] xl:gap-4">
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7">
          {days.map((label, day) => (
            <Card key={label} className="border-border bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm capitalize">{label}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(byDay[day] || []).map(({ slot, idx }) => {
                  const key = slotKey(slot, idx);
                  const selectedNow = selectedKey === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedKey(key)}
                      className={cn(
                        "flex w-full flex-col gap-1 rounded-md border border-slate-200 bg-[#fafbfc] px-2 py-2 text-left transition hover:border-slate-300",
                        selectedNow && "border-brand bg-white ring-2 ring-brand",
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <Badge variant={funnelBadgeVariant(slot.funnel_stage)} className="px-1.5 py-0 text-[10px]">
                          {(slot.funnel_stage || "?").toUpperCase()}
                        </Badge>
                        <span className="text-xs font-medium">{kindShort(slot.kind)}</span>
                        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                          {slot.owner === "kasia" ? "K" : "A"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span
                          className={cn(
                            "h-2 w-2 rounded-full",
                            slot.hope_axis === "echo"
                              ? "bg-destructive/70"
                              : slot.hope_axis === "wings"
                                ? "bg-emerald-600"
                                : "bg-brand/70",
                          )}
                          title={hopeTitle(slot.hope_axis)}
                        />
                        <span>{hopeTitle(slot.hope_axis)}</span>
                        {statusShort(slot.status) ? <span>· {statusShort(slot.status)}</span> : null}
                      </div>
                    </button>
                  );
                })}
                <Button size="sm" variant="outline" className="w-full" onClick={() => addSlot(day)}>
                  +
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <aside className="mt-4 xl:mt-0 xl:sticky xl:top-4 xl:h-fit">{editor}</aside>
      </div>
    </div>
  );
}
